import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Container, Form, Header, Segment } from 'semantic-ui-react';
import { agent } from '../../app/api/agent';
import { useTranslation } from 'react-i18next';

export const ResetPasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    // Get the raw URL parameters (still URL-encoded)
    const urlToken = searchParams.get('token');
    const urlEmail = searchParams.get('email');
    
    console.log('Raw URL token:', urlToken);
    console.log('Raw URL email:', urlEmail);
    
    if (!urlToken || !urlEmail) {
      toast.error(t('resetPassword.errors.invalidResetLink'));
      navigate('/');
      return;
    }
    
    try {
      // Both token and email are Base64 encoded
      // searchParams.get() already URL-decodes them, so we just need to Base64 decode
      const decodedToken = atob(urlToken);
      const decodedEmail = atob(urlEmail);
      
      console.log('Decoded token length:', decodedToken.length);
      console.log('Decoded token (first 50):', decodedToken.substring(0, 50));
      console.log('Decoded email:', decodedEmail);
      
      setToken(decodedToken);
      setEmail(decodedEmail);
    } catch (error) {
      console.error('Failed to decode token or email:', error);
      toast.error(t('resetPassword.errors.invalidResetLink'));
      navigate('/');
    }
  }, [searchParams, navigate, t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error(t('resetPassword.errors.passwordsDoNotMatch'));
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error(t('resetPassword.errors.passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email,
        token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      };
      
      console.log('Sending reset password request');
      console.log('Email:', email);
      console.log('Token length:', token.length);
      
      await agent.Account.resetPassword(payload);
      
      toast.success(t('resetPassword.success.resetSuccessful'));
      navigate('/login');
    } catch (error: any) {
      console.error('Reset password error:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = t('resetPassword.errors.resetFailed');
      
      if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors[0];
      } else if (error.response?.data?.Errors) {
        errorMessage = error.response.data.Errors[0];
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        errorMessage = typeof error.response.data === 'string' 
          ? error.response.data 
          : JSON.stringify(error.response.data);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <Container style={{ maxWidth: '450px', marginTop: '50px' }}>
        <Segment padded>
          <Header as="h2" content={t('resetPassword.invalidLink.title')} color="red" textAlign="center" />
          <p style={{ textAlign: 'center' }}>
            {t('resetPassword.invalidLink.description')}
          </p>
        </Segment>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: '450px', marginTop: '50px' }}>
      <Segment padded>
        <Header as="h2" content={t('resetPassword.title')} color="teal" textAlign="center" />
        <p style={{ textAlign: 'center', marginBottom: '30px' }}>
          {t('resetPassword.description')} {email}
        </p>
        
        <Form onSubmit={handleSubmit}>
          <Form.Field required>
            <label>{t('resetPassword.form.newPasswordLabel')}</label>
            <Form.Input
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              placeholder={t('resetPassword.form.newPasswordPlaceholder')}
              disabled={loading}
            />
          </Form.Field>
          
          <Form.Field required>
            <label>{t('resetPassword.form.confirmPasswordLabel')}</label>
            <Form.Input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder={t('resetPassword.form.confirmPasswordPlaceholder')}
              disabled={loading}
            />
          </Form.Field>
          
          <Button 
            type="submit" 
            primary
            fluid
            loading={loading}
            disabled={loading}
            content={loading ? t('resetPassword.form.submitting') : t('resetPassword.form.submitButton')}
            style={{ marginTop: '20px' }}
          />
        </Form>
      </Segment>
    </Container>
  );
};